import React, { useContext, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardBody, Row, Col, Badge, Collapse } from "reactstrap";
import styles from "../../../../assets/scss/common/viewDetails.module.scss";
import skeletonStyles from "../../../../assets/scss/common/viewSkeleton.module.scss";
import SimpleBar from "simplebar-react";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { studySections} from "helpers/constant";
import { BreadcrumbContext } from "Layouts/Header";
import { STUDY_PATHS } from "Routes/paths";
import { labels, renderStudyValue, truncateWithTooltip } from "../helpers/studydetails.constant";
import ViewSkeleton from "Components/Common/ViewSkeleton";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { activitycolumns } from "modules/Clinical/AmbulatoryVisit/helper/ambulatoryvisitconstants";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { normalizeStudyChecklists, viewchecklistscolumns } from "modules/Study/Study/helpers/studyconstant";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";
import { formatDate } from "common/service/CommonService";
import { AMBULATORY_VISIT_TYPE_OPTIONS, AMBULATORY_VISIT_CATEGORY_OPTIONS } from "api/modules/common.option";

/* -----------------------------
 Types
----------------------------- */
interface Props {
  setnumberofsubject: (value: number) => void;
}

/* ---------- Sub-component for Checklist Assignment View Mode ---------- */
const ChecklistAssignmentViewSection: React.FC<{ studyData: any }> = ({ studyData }) => {
  const numPeriods = Number(studyData?.numberofperiods) || 1;

  const [selectedScope, setSelectedScope] = useState<"pre_enrollment" | "period_based">("pre_enrollment");
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [selectedType, setSelectedType] = useState<number | null>(null);

  const allStudyChecklists: any[] = useMemo(() => {
    if (!studyData || !studyData.studychecklists) return [];
    return normalizeStudyChecklists(studyData.studychecklists);
  }, [studyData]);

  const hasInclusionExclusionCriteria = useMemo(() => {
    return allStudyChecklists.some((item: any) => {
      const text = String(
        item.checklisttypename || item.checklisttype_name || item.checklisttypetext || item.checklisttype_text || ""
      ).toLowerCase();
      return text.includes("inclusion") || text.includes("exclusion");
    });
  }, [allStudyChecklists]);

  const isPreEnrollmentType = (text: string) => {
    const t = text.toLowerCase();
    return (
      t.includes("inclusion") ||
      t.includes("exclusion") ||
      t.includes("pre enrollment") ||
      t.includes("pre-enrollment")
    );
  };

  const rawLists = useMemo(() => {
    if (!allStudyChecklists.length) return [];

    if (hasInclusionExclusionCriteria && selectedScope === "pre_enrollment") {
      return allStudyChecklists.filter((item: any) => {
        const text = String(
          item.checklisttypename || item.checklisttype_name || item.checklisttypetext || item.checklisttype_text || ""
        );
        return isPreEnrollmentType(text);
      });
    }

    let targetPeriodId: number | null = null;
    if (Array.isArray(studyData.periods)) {
      const pObj = studyData.periods.find((p: any) => Number(p.period_no) === selectedPeriod);
      if (pObj) targetPeriodId = Number(pObj.period_id);
    }

    return allStudyChecklists.filter((item: any) => {
      const text = String(
        item.checklisttypename || item.checklisttype_name || item.checklisttypetext || item.checklisttype_text || ""
      );
      if (hasInclusionExclusionCriteria && isPreEnrollmentType(text)) {
        return false;
      }
      const itemSort = Number(item.period_sort);
      if (itemSort === selectedPeriod) return true;
      if (targetPeriodId !== null && Number(item.periodid) === targetPeriodId) return true;
      return false;
    });
  }, [allStudyChecklists, selectedPeriod, studyData, hasInclusionExclusionCriteria, selectedScope]);

  const typeOptions = useMemo(() => {
    const map = new Map<number, { label: string; count: number }>();
    rawLists.forEach((item: any) => {
      const typeVal = Number(item.checklisttype);
      const label =
        item.checklisttypename ||
        item.checklisttype_name ||
        item.checklisttypetext ||
        item.checklisttype_text ||
        `Type ${typeVal}`;
      if (typeVal) {
        const existing = map.get(typeVal);
        map.set(typeVal, {
          label,
          count: (existing?.count || 0) + 1,
        });
      }
    });
    return Array.from(map.entries()).map(([value, info]) => ({
      value,
      label: info.label,
      count: info.count,
    }));
  }, [rawLists]);

  useEffect(() => {
    if (typeOptions.length > 0 && (!selectedType || !typeOptions.some((t: any) => t.value === selectedType))) {
      setSelectedType(typeOptions[0].value);
    }
  }, [typeOptions, selectedType, selectedScope]);

  const activeQuestions = useMemo(() => {
    if (!selectedType) return [];
    return rawLists.filter((item: any) => Number(item.checklisttype) === selectedType);
  }, [rawLists, selectedType]);

  const activeTypeLabel = typeOptions.find((t: any) => t.value === selectedType)?.label || "";

  return (
    <Col lg="12">
      {/* Scope Selector Chips (if Inclusion/Exclusion exist) */}
      {hasInclusionExclusionCriteria && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Checklist Scope:</div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedScope("pre_enrollment")}
              className={`btn btn-sm ${selectedScope === "pre_enrollment" ? "btn-primary" : "btn-soft-secondary"
                } rounded-pill px-3 d-flex align-items-center gap-1`}
            >
              <i className="ri-file-text-line me-1" /> Enrollment Checklists
            </button>
            <button
              type="button"
              onClick={() => setSelectedScope("period_based")}
              className={`btn btn-sm ${selectedScope === "period_based" ? "btn-primary" : "btn-soft-secondary"
                } rounded-pill px-3 d-flex align-items-center gap-1`}
            >
              <i className="ri-calendar-line me-1" /> Study Period Checklists
            </button>
          </div>
        </div>
      )}

      {/* Period Selector (Only when Period-Based or no Inclusion/Exclusion) */}
      {(!hasInclusionExclusionCriteria || selectedScope === "period_based") && numPeriods > 1 && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Select Period:</div>
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: numPeriods }, (_, i) => i + 1).map((p) => {
              const isSelected = selectedPeriod === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSelectedPeriod(p)}
                  className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-soft-secondary"
                    } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  Period {p}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {typeOptions.length > 0 ? (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Checklist Types:</div>
          <div className="d-flex flex-wrap gap-2">
            {typeOptions.map((type: any) => {
              const isSelected = selectedType === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-outline-secondary"
                    } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  <span>{type.label}</span>
                  <span className="badge bg-light text-primary rounded-circle ms-1">
                    {type.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-muted fs-14 py-2">No checklists configured for this view.</div>
      )}

      {activeQuestions.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-bold text-dark mb-2">
            {activeTypeLabel} ({activeQuestions.length} Items)
          </h6>
          <CommonTableIndexWise
            columns={viewchecklistscolumns}
            data={activeQuestions}
            paginationShow={false}
            hideAction={true}
            showStatus={false}
          />
        </div>
      )}
    </Col>
  );
};

/* ---------- Sub-component for CRF Assignment View Mode ---------- */
const CRFAssignmentViewSection: React.FC<{ studyData: any }> = ({ studyData }) => {
  const numPeriods = Number(studyData?.numberofperiods) || 1;

  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const { CategorySelectAllQuery } = useCRFService({
    ispagination: false,
  });

  const categoryLookup = useMemo(() => {
    const modules = CategorySelectAllQuery?.data?.data || [];
    const map = new Map<number, string>();
    modules.forEach((item: any) => {
      map.set(Number(item.moduleid), item.modulename);
    });
    return map;
  }, [CategorySelectAllQuery]);

  const rawLists = useMemo(() => {
    if (!studyData || !Array.isArray(studyData.studycrfs)) return [];

    let targetPeriodId: number | null = null;
    if (Array.isArray(studyData.periods)) {
      const pObj = studyData.periods.find((p: any) => Number(p.period_no) === selectedPeriod);
      if (pObj) targetPeriodId = Number(pObj.period_id);
    }

    return studyData.studycrfs.filter((item: any) => {
      const itemSort = Number(item.period_sort);
      if (itemSort === selectedPeriod) return true;
      if (targetPeriodId !== null && Number(item.periodid) === targetPeriodId) return true;
      return false;
    });
  }, [studyData, selectedPeriod]);

  const categoryOptions = useMemo(() => {
    const map = new Map<number, { label: string; count: number }>();
    rawLists.forEach((item: any) => {
      const catVal = Number(item.crfmodule || item.moduleid || item.crfcategory);
      if (catVal) {
        const label = categoryLookup.get(catVal) || item.modulename || item.categoryname || `Category ${catVal}`;
        const existing = map.get(catVal);
        map.set(catVal, {
          label,
          count: (existing?.count || 0) + 1,
        });
      }
    });
    return Array.from(map.entries()).map(([value, info]) => ({
      value,
      label: info.label,
      count: info.count,
    }));
  }, [rawLists, categoryLookup]);

  useEffect(() => {
    if (categoryOptions.length > 0 && (!selectedCategory || !categoryOptions.some((t: any) => t.value === selectedCategory))) {
      setSelectedCategory(categoryOptions[0].value);
    }
  }, [categoryOptions, selectedCategory]);

  const activeCRFs = useMemo(() => {
    if (!selectedCategory) return [];
    return rawLists.filter((item: any) => Number(item.crfmodule || item.moduleid || item.crfcategory) === selectedCategory);
  }, [rawLists, selectedCategory]);

  const activeCategoryLabel = categoryOptions.find((t: any) => t.value === selectedCategory)?.label || "";

  const crfViewColumns = [
    {
      key: "crfname",
      label: "CRF Name",
      render: (val: string, row: any) => (
        <span className="fw-medium text-dark">
          {val || row.crfname || row.crfName || row.name || `CRF #${row.crfid}`}
        </span>
      ),
    },
  ];

  return (
    <Col lg="12">
      {numPeriods > 1 && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Select Period:</div>
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: numPeriods }, (_, i) => i + 1).map((p) => {
              const isSelected = selectedPeriod === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSelectedPeriod(p)}
                  className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-soft-secondary"
                    } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  Period {p}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {categoryOptions.length > 0 ? (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">CRF Categories:</div>
          <div className="d-flex flex-wrap gap-2">
            {categoryOptions.map((cat: any) => {
              const isSelected = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-outline-secondary"
                    } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  <span>{cat.label}</span>
                  <span className="badge bg-light text-primary rounded-circle ms-1">
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-muted fs-14 py-2">No CRFs configured.</div>
      )}

      {activeCRFs.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-bold text-dark mb-2">
            {activeCategoryLabel} ({activeCRFs.length} Items)
          </h6>
          <CommonTableIndexWise
            moduleName={PERMISSION_MODULES.STUDY}
            columns={crfViewColumns}
            data={activeCRFs}
            paginationShow={false}
            hideAction={true}
            showStatus={false}
          />
        </div>
      )}
    </Col>
  );
};

const StudyDetails: React.FC<Props> = ({ setnumberofsubject }) => {
  const location = useLocation();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const studyId = Number(sessionStorage.getItem("activestudyid"));

  const { SelectQuery } = useMasterStudyService({
    id: studyId,
    enabledSelect: true,
  });

  const { data, isLoading, isFetching } = SelectQuery;

  const [study, setStudy] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<number>(1);
  const [openVisitIndex, setOpenVisitIndex] = useState<number | null>(null);

  useEffect(() => {
    const isScreening = location.pathname.includes("/study/screening");
    breadcrumbContext?.setBreadcrumbItems?.([
      isScreening
        ? { text: "Study Screening", link: STUDY_PATHS.SCREENING_LIST }
        : { text: "Study", link: STUDY_PATHS.LIST },
      { text: "Study Details" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [location.pathname, breadcrumbContext]);
  const [icfModal, setIcfModal] = useState(false);

  // Helper to scroll
  const scrollToSection = (id: number) => {
    setActiveTab(id);
    const element = document.getElementById(`section-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  /* -----------------------------
     Bind API Response
  ----------------------------- */

  useEffect(() => {
    if (data?.data) {
      const apiStudy = data?.data[0] || {};

      // Merge all apiStudy keys in case it brings everything natively.
      // But we explicitly remap text equivalents for fields returning IDs
      setStudy({
        ...apiStudy,
        studydesign: apiStudy.studydesigntext || apiStudy.studydesign,
        gender: apiStudy.gendertext || apiStudy.gender,
        fastingcondition: apiStudy.fastingconditiontext || apiStudy.fastingcondition,
        routeofadministration: apiStudy.routeofadministrationtext || apiStudy.routeofadministration,
        lightcondition: apiStudy.lightconditiontext || apiStudy.lightcondition,
        biologicalmatrix: apiStudy.biologicalmatrixtext || apiStudy.biologicalmatrix,
        washoutdays: apiStudy.washoutdays || apiStudy.washoutperiod,

      });

      setnumberofsubject(apiStudy.totalsubjects ?? 0);
    }
  }, [data, setnumberofsubject]);

  /* -----------------------------
     Render
  ----------------------------- */

  if (isLoading || !study || isFetching) {
    return (
      <>
        <div className="mt-3">
          <Row className="g-3">
            {/* SKELETON SIDEBAR */}
            <Col lg="3" md="4" className="d-none d-md-block" style={{ position: "sticky", top: "80px", zIndex: 10 }}>
              <Card className="border-0 shadow-sm mb-0 rounded-4" style={{ overflow: "hidden" }}>
                <div className="bg-light px-4 py-3 border-bottom border-light">
                  <div className={`${skeletonStyles.skeletonLine} w-50`} style={{ height: "12px", borderRadius: "4px" }}></div>
                </div>
                <CardBody className="p-3 bg-white">
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                    {Array.from({ length: studySections.length }).map((_, i) => (
                      <li key={i} className="px-3 py-2 rounded-3 d-flex align-items-center" style={{ minHeight: "42px" }}>
                        <div className={`${skeletonStyles.skeletonLine} w-75`} style={{ height: "14px", borderRadius: "4px" }}></div>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Col>

            {/* SKELETON MAIN CONTENT */}
            <Col lg="9" md="8">
              <SimpleBar style={{ height: "calc(100vh - 110px)" }}>
                <div className="d-flex flex-column gap-3 mb-0 pe-2 pt-1 pb-1">
                  <ViewSkeleton sections={4} fieldsPerSection={8} />
                </div>
              </SimpleBar>
            </Col>
          </Row>
        </div>
      </>
    );
  }

  return (
    <>


      <div className="mt-3">
        <Row className="g-3">
          {/* SIDEBAR NAVIGATION */}
          <Col lg="3" md="4" className="d-none d-md-block" style={{ position: "sticky", top: "80px", zIndex: 10 }}>
            <Card className="border-0 shadow-sm rounded-4 w-100 mb-0 overflow-hidden d-flex flex-column" style={{ height: "100%" }}>
              <CardBody className="p-0 d-flex flex-column" style={{ minHeight: 0, flex: 1 }}>
                <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                  {studySections.map(section => {
                    const isActive = activeTab === section.id;
                    return (
                      <li
                        key={section.id}
                        className={`px-3 py-2 rounded-3 d-flex align-items-center fw-medium ${!isActive ? styles.sectionHeader : styles.activeSection} `}
                        onClick={() => scrollToSection(section.id)}
                      >
                        <i className={`${section.icon} fs-18 me-3`} style={{ color: isActive ? "#24465a" : "#878a99" }}></i>
                        <span className="flex-grow-1">{section.title}</span>
                        {isActive && <i className="ri-arrow-right-s-line fs-18 opacity-50"></i>}
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </Col>

          {/* MAIN CONTENT CARDS */}
          <Col lg="9" md="8">
            <SimpleBar style={{ height: "calc(100vh - 110px)" }}>
              <div className="d-flex flex-column gap-3 mb-0 pe-2 pt-1 pb-1  test">
                {studySections.map((section) => (
                  <Card id={`section-${section.id}`} key={section.id} className={`${styles.reviewBox} h-100 mb-0`}>
                    <CardBody>
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                        <h6 className="fw-bold text-primary mb-0 d-flex align-items-center">
                          <i className={`${section.icon} me-2`}></i> {section.title}
                        </h6>
                      </div>

                      {/* Fields */}
                      <Row className="g-3">
                        {section.title === "Checklist Assignment" ? (
                          <ChecklistAssignmentViewSection studyData={study} />
                        ) : section.title === "CRF Assignment" ? (
                          <CRFAssignmentViewSection studyData={study} />
                        ) : section.title === "Ambulatory Visit" ? (
                          (() => {
                            if (!study?.visits || !Array.isArray(study.visits) || study.visits.length === 0) {
                              return (
                                <Col lg="12">
                                  <div className="text-muted text-center py-4">
                                    <i className="ri-information-line fs-20 align-middle me-2"></i>
                                    No Ambulatory Visits configured.
                                  </div>
                                </Col>
                              );
                            }

                            const map = new Map<string, any>();

                            study.visits.forEach((v: any) => {
                              const key = String(v.visitname || v.visitnumber || v.visitid).toLowerCase().trim();
                              const periodStr = String(v.period_number ?? v.period_no ?? "01").padStart(2, "0");

                              let spList: string[] = [];
                              if (Array.isArray(v.samplingpoints)) {
                                spList = v.samplingpoints.map((s: any) => String(s));
                              } else if (typeof v.samplingpoints === "string" && v.samplingpoints.trim()) {
                                spList = [v.samplingpoints.trim()];
                              }

                              if (!map.has(key)) {
                                map.set(key, {
                                  ...v,
                                  periodsSet: new Set<string>([periodStr]),
                                  spSet: new Set<string>(spList),
                                });
                              } else {
                                const existing = map.get(key);
                                existing.periodsSet.add(periodStr);
                                spList.forEach((sp: string) => existing.spSet.add(sp));
                              }
                            });

                            const consolidatedVisits = Array.from(map.values()).map((item: any) => {
                              const periods = Array.from(item.periodsSet as Set<string>).sort();
                              const samplingPoints = Array.from(item.spSet as Set<string>).sort((a, b) => {
                                const nA = parseFloat(a);
                                const nB = parseFloat(b);
                                return !isNaN(nA) && !isNaN(nB) ? nA - nB : a.localeCompare(b);
                              });

                              return {
                                ...item,
                                periodDisplay: periods.join(", "),
                                samplingPointsList: samplingPoints,
                                totalSamplingPoints: samplingPoints.length,
                              };
                            });

                            return consolidatedVisits.map((visit: any, index: number) => {
                              return (
                                <React.Fragment key={visit.visitid || index}>
                                  <div className="px-0 mb-3" style={{ borderRadius: "10px", backgroundColor: "#f7f9faff", width: "100%" }}>
                                    <Col lg="12" className="pt-2">
                                      <div className="px-3">
                                        <Row className="px-1 mt-2" style={{ borderRadius: "10px" }}>
                                          {[
                                            { key: "visitname", label: "Visit Name" },
                                            { key: "visittypename", label: "Visit Type" },
                                            { key: "visitcategoryname", label: "Visit Category" },
                                            { key: "period_number", label: "Period Number" },
                                            { key: "ambulatorytotalsamplingpoints", label: "Total Sampling Point" },
                                            { key: "visitwindowdeviation", label: "Allowed Deviation Hours" },
                                            { key: "visitdescription", label: "Visit Description" },
                                          ].map((f) => {
                                            let displayVal = "—";
                                            if (f.key === "period_number") {
                                              displayVal = visit.periodDisplay || "—";
                                            } else if (f.key === "visittypename") {
                                              displayVal = visit.visittypename || AMBULATORY_VISIT_TYPE_OPTIONS.find(o => String(o.value) === String(visit.visittype))?.label || (visit.visittype !== undefined && visit.visittype !== null && visit.visittype !== "" ? String(visit.visittype) : "—");
                                            } else if (f.key === "visitcategoryname") {
                                              displayVal = visit.visitcategoryname || AMBULATORY_VISIT_CATEGORY_OPTIONS.find(o => String(o.value) === String(visit.visitcategory))?.label || (visit.visitcategory !== undefined && visit.visitcategory !== null && visit.visitcategory !== "" ? String(visit.visitcategory) : "—");
                                            } else if (f.key === "ambulatorytotalsamplingpoints") {
                                              displayVal = visit.totalSamplingPoints ? String(visit.totalSamplingPoints) : (visit.ambulatorytotalsamplingpoints || "—");
                                            } else if (f.key === "visitwindowdeviation") {
                                              displayVal = visit.visitwindowdeviation || visit.deviation || "—";
                                            } else if (visit[f.key] !== undefined && visit[f.key] !== null && visit[f.key] !== "") {
                                              displayVal = visit[f.key];
                                            }

                                            return (
                                              <Col lg="4" key={f.key} className="mt-2 mb-3">
                                                <div className={styles.reviewItem}>
                                                  <div className={styles.reviewLabel}>{f.label}</div>
                                                  <div className={styles.reviewValue}>{displayVal}</div>
                                                </div>
                                              </Col>
                                            );
                                          })}
                                        </Row>

                                        {visit.samplingPointsList && visit.samplingPointsList.length > 0 && (
                                          <Row className="px-1 mt-1 mb-3">
                                            {visit.samplingPointsList.map((sp: any, i: number) => (
                                              <Col lg="3" key={i} className="mt-2 mb-3">
                                                <div className={styles.reviewItem}>
                                                  <div className={styles.reviewLabel}>Sampling Point {i + 1}</div>
                                                  <div className={styles.reviewValue}>{sp}</div>
                                                </div>
                                              </Col>
                                            ))}
                                          </Row>
                                        )}
                                      </div>
                                    </Col>
                                  </div>
                                </React.Fragment>
                              );
                            });
                          })()
                        ) : (
                          section.keys.map((key) => {
                            const value = study?.[key];

                            if (section.title === "Investigational Product") {
                              if (key === "testdrugname") {
                                return (
                                  <React.Fragment key="test-product">
                                    <Col lg="12">
                                      <h6 className="text-primary mb-2 mt-2">
                                        Test Drug
                                      </h6>
                                    </Col>
                                    {[
                                      "testdrugname",
                                      "testdosageform",
                                      "teststrength",
                                      "testbatchno",
                                      "testmanufacturer",
                                      "testmanufacturingdate",
                                      "testexpirydate",
                                    ].map((testKey) => (
                                      <Col lg="3" md="6" sm="6" xs="12" key={testKey}>
                                        <div className={styles.reviewItem}>
                                          <div className={styles.reviewLabel}>
                                            {labels[testKey as keyof typeof labels] || testKey}
                                          </div>
                                          <div className={styles.reviewValue}>
                                            {study ? (
                                              truncateWithTooltip(renderStudyValue(testKey, study[testKey]))
                                            ) : (
                                              <span className="text-muted">—</span>
                                            )}
                                          </div>
                                        </div>
                                      </Col>
                                    ))}
                                  </React.Fragment>
                                );
                              } else if (key === "refdrugname") {
                                return (
                                  <React.Fragment key="reference-product">
                                    <Col lg="12">
                                      <h6 className="text-primary mb-2 mt-3">
                                        Reference Drug
                                      </h6>
                                    </Col>
                                    {[
                                      "refdrugname",
                                      "refdosageform",
                                      "refstrength",
                                      "refbatchno",
                                      "refmanufacturer",
                                      "refmanufacturingdate",
                                      "refexpirydate",
                                    ].map((refKey) => (
                                      <Col lg="3" md="6" sm="6" xs="12" key={refKey}>
                                        <div className={styles.reviewItem}>
                                          <div className={styles.reviewLabel}>
                                            {labels[refKey as keyof typeof labels] || refKey}
                                          </div>
                                          <div className={styles.reviewValue}>
                                            {study ? (
                                              truncateWithTooltip(renderStudyValue(refKey, study[refKey]))
                                            ) : (
                                              <span className="text-muted">—</span>
                                            )}
                                          </div>
                                        </div>
                                      </Col>
                                    ))}
                                  </React.Fragment>
                                );
                              } else if (
                                [
                                  "testdosageform",
                                  "teststrength",
                                  "testbatchno",
                                  "testmanufacturer",
                                  "testmanufacturingdate",
                                  "testexpirydate",
                                  "refdosageform",
                                  "refstrength",
                                  "refbatchno",
                                  "refmanufacturer",
                                  "refmanufacturingdate",
                                  "refexpirydate",
                                ].includes(key as string)
                              ) {
                                return null; // Already handled above
                              }
                            }

                            if (section.title === "Study Timeline" && key === "periods" && study?.periods?.length > 0) {
                              return (
                                <>
                                  <hr className="mb-0" />
                                  {study.periods.map((p: any, i: number) => (
                                    <>
                                      <Col lg="3" key={`period-${i}`} >
                                        <div className={styles.reviewItem}>
                                          <div className={styles.reviewLabel} >
                                            Period
                                          </div>
                                          <div className={styles.reviewValue} >
                                            Period {p.period_no}
                                          </div>
                                        </div>
                                      </Col>

                                      <Col lg="3" key={`period-${i}`}>
                                        <div className={styles.reviewItem} >
                                          <div className={styles.reviewLabel} >
                                            Period {p.period_no} Start Date
                                          </div>
                                          <div className={styles.reviewValue} >
                                            {renderStudyValue(`Start Date`, p.start_date)}
                                          </div>
                                        </div>
                                      </Col>

                                      <Col lg="3" key={`period-${i}`}>
                                        <div className={styles.reviewItem} >
                                          <div className={styles.reviewLabel} >
                                            Period {p.period_no} Check-in Date
                                          </div>
                                          <div className={styles.reviewValue} >
                                            {renderStudyValue(`Check-in Date`, p.checkin_date)}
                                          </div>
                                        </div>
                                      </Col>

                                      <Col lg="3" key={`period-${i}`}>
                                        <div className={styles.reviewItem} >
                                          <div className={styles.reviewLabel} >
                                            Period {p.period_no} Check-out Date
                                          </div>
                                          <div className={styles.reviewValue} >
                                            {renderStudyValue(`Check-out Date`, p.checkout_date)}
                                          </div>
                                        </div>
                                      </Col>

                                      {study.periods.length > i + 1 && <hr className="mb-0" />}
                                    </>
                                  ))}

                                </>
                              )
                            }

                            return (
                              <Col lg="3" md="6" sm="6" xs="12" key={key}>
                                <div className={styles.reviewItem}>
                                  <div className={styles.reviewLabel} >
                                    {labels[key] || key as string}
                                  </div>

                                  <div className={styles.reviewValue}>
                                    {study ? (
                                      truncateWithTooltip(renderStudyValue(key, value),)
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )
                                    }
                                  </div>
                                </div>
                              </Col>
                            );

                          }))}
                      </Row>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </SimpleBar>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default StudyDetails;
